import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaGift,
  FaEnvelope,
  FaDollarSign,
  FaLock,
  FaPlus,
  FaCheckCircle,
  FaExclamationCircle,
  FaTimes,
} from "react-icons/fa";
import { useAdmin } from "../context/AdminContext";
import Alert from "../components/Alert";

const AddBonus = () => {
  const { addUserBonus, alert, showAlert } = useAdmin();

  const [email, setEmail] = useState("");
  const [bonus, setBonus] = useState("");
  const [password, setPassword] = useState("");

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // Confirmation modal
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();

    setMessage("");
    setError("");

    const amount = Number(bonus);

    if (!email.trim()) {
      setError("Please enter user email.");
      return;
    }

    if (!amount || amount <= 0) {
      setError("Please enter a valid bonus amount.");
      return;
    }

    if (!password) {
      setError("Please enter admin password.");
      return;
    }

    // Only open confirmation modal
    setShowConfirmModal(true);
  };

  const handleConfirmBonus = async () => {
    try {
      setLoading(true);
      setShowConfirmModal(false);

      const success = await addUserBonus(
        email.trim(),
        bonus,
        password
      );

      if (success) {
        setEmail("");
        setBonus("");
        setPassword("");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (!loading) {
      setShowConfirmModal(false);
    }
  };

  return (
    <div className="w-full max-w-xl mx-auto relative">
      {/* Alert */}
      <Alert
        type={alert.type}
        message={alert.message}
        isOpen={alert.isOpen}
        onClose={() =>
          showAlert({
            isOpen: false,
            type: "",
            message: "",
          })
        }
      />

      {/* Main Card */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-[#0b1220] border border-slate-800 rounded-2xl p-6 shadow-xl"
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-yellow-500/10 border border-yellow-500/20 flex items-center justify-center">
            <FaGift className="text-yellow-400 text-lg" />
          </div>

          <div>
            <h2 className="text-xl font-bold text-white">
              Add User Bonus
            </h2>

            <p className="text-sm text-slate-400">
              Add bonus directly to user's earnings
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              User Email
            </label>

            <div className="relative">
              <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />

              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
                className="w-full bg-[#070d18] border border-slate-700 rounded-xl pl-11 pr-4 py-3 text-white outline-none focus:border-yellow-500 transition"
              />
            </div>
          </div>

          {/* Bonus */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Bonus Amount
            </label>

            <div className="relative">
              <FaDollarSign className="absolute left-4 top-1/2 -translate-y-1/2 text-green-400" />

              <input
                type="number"
                min="0"
                step="0.01"
                max="150"
                value={bonus}
                onChange={(e) => setBonus(e.target.value)}
                placeholder="100"
                className="w-full bg-[#070d18] border border-slate-700 rounded-xl pl-11 pr-4 py-3 text-white outline-none focus:border-green-500 transition"
              />
            </div>
          </div>

          {/* Admin Password */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Admin Password
            </label>

            <div className="relative">
              <FaLock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />

              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="w-full bg-[#070d18] border border-slate-700 rounded-xl pl-11 pr-4 py-3 text-white outline-none focus:border-red-500 transition"
              />
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl p-3 text-sm">
              <FaExclamationCircle />
              <span>{error}</span>
            </div>
          )}

          {/* Success */}
          {message && (
            <div className="flex items-center gap-2 bg-green-500/10 border border-green-500/20 text-green-400 rounded-xl p-3 text-sm">
              <FaCheckCircle />
              <span>{message}</span>
            </div>
          )}

          {/* Submit */}
          <motion.button
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold py-3 rounded-xl transition"
          >
            <FaPlus />

            {loading ? "Adding Bonus..." : "Add Bonus"}
          </motion.button>
        </form>
      </motion.div>

      {/* Confirmation Modal */}
      <AnimatePresence>
        {showConfirmModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="w-full max-w-md bg-[#0b1220] border border-slate-700 rounded-2xl shadow-2xl overflow-hidden"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-5 border-b border-slate-800">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-yellow-500/10 border border-yellow-500/20 flex items-center justify-center">
                    <FaGift className="text-yellow-400" />
                  </div>

                  <div>
                    <h3 className="text-lg font-bold text-white">
                      Confirm Bonus
                    </h3>

                    <p className="text-xs text-slate-400">
                      Please review before continuing
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleCancel}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-800 transition"
                >
                  <FaTimes />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-5">
                <p className="text-slate-300 text-sm mb-5">
                  Are you sure you want to add this bonus to the user's
                  earnings?
                </p>

                <div className="bg-[#070d18] border border-slate-800 rounded-xl p-4 space-y-3">
                  <div className="flex justify-between gap-4">
                    <span className="text-slate-500 text-sm">
                      User
                    </span>

                    <span className="text-white text-sm font-medium break-all text-right">
                      {email}
                    </span>
                  </div>

                  <div className="h-px bg-slate-800" />

                  <div className="flex justify-between items-center">
                    <span className="text-slate-500 text-sm">
                      Bonus
                    </span>

                    <span className="text-green-400 text-xl font-bold">
                      ${Number(bonus).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex gap-3 p-5 border-t border-slate-800">
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={loading}
                  className="flex-1 py-3 rounded-xl border border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white transition font-medium disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={handleConfirmBonus}
                  disabled={loading}
                  className="flex-1 py-3 rounded-xl bg-yellow-500 hover:bg-yellow-400 text-black font-bold transition disabled:opacity-50"
                >
                  {loading ? "Adding..." : "Confirm Bonus"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AddBonus;